import { useRef, useState } from "react";
import { Upload, X, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

/**
 * Manages an ordered list of image URLs (uploaded or external).
 * value: string[] (URLs/paths)
 * onChange: (string[]) => void
 * The first item is treated as the primary image.
 */
export default function MultiImageUploader({ value = [], onChange, label = "Images", recommended = "" }) {
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const addUrl = () => {
    if (!urlInput.trim()) return;
    onChange([...value, urlInput.trim()]);
    setUrlInput("");
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const next = [...value];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(res.data.url);
      }
      onChange(next);
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = (i) => {
    const next = [...value]; next.splice(i, 1); onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value]; [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  };
  const setPrimary = (i) => {
    if (i === 0) return;
    const next = [...value]; const [picked] = next.splice(i, 1); next.unshift(picked); onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-brand-soft">
          {label}
          {recommended && <span className="ml-2 text-xs text-brand-soft/50 font-normal">{recommended}</span>}
        </label>
        <div className="text-xs text-brand-soft/50">{value.length} image{value.length === 1 ? "" : "s"}</div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={onPick} />
        <button type="button" onClick={() => fileInput.current?.click()} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-brand-ivory rounded-xl text-sm hover:bg-brand-maroon/10 transition disabled:opacity-60">
          <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload images"}
        </button>
        <span className="text-xs text-brand-soft/40">or</span>
        <input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
          placeholder="Paste image URL"
          className="flex-1 min-w-[200px] bg-brand-ivory rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
        />
        <button type="button" onClick={addUrl} className="px-4 py-2 rounded-xl bg-brand-maroon text-brand-cream text-sm hover:bg-brand-maroon-dark">Add URL</button>
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((url, i) => (
            <div key={i} className="relative group rounded-xl overflow-hidden border border-brand-maroon/10 bg-brand-ivory aspect-square">
              <img src={fileUrl(url)} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <div className="absolute top-2 left-2 bg-brand-maroon text-brand-cream text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1">
                  <Star className="w-2.5 h-2.5 fill-current" /> Primary
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move left" className="p-1.5 bg-white rounded-full disabled:opacity-30"><ArrowLeft className="w-3 h-3" /></button>
                {i !== 0 && (
                  <button type="button" onClick={() => setPrimary(i)} title="Set primary" className="p-1.5 bg-white rounded-full"><Star className="w-3 h-3" /></button>
                )}
                <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} title="Move right" className="p-1.5 bg-white rounded-full disabled:opacity-30"><ArrowRight className="w-3 h-3" /></button>
                <button type="button" onClick={() => remove(i)} title="Remove" className="p-1.5 bg-red-50 text-red-700 rounded-full"><X className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
